import type { BackupCipher } from "../../../../../supabase/functions/_shared/backupTypes";
import type { LocalBackupSource, LocalSnapshot } from "./repository";

export type BackupStage = "snapshot" | "encrypt";
export type RestoredBackup = { snapshot: LocalSnapshot; sqliteImage?: string; schemaVersion?: number };
export type BackupCryptoRequest =
  | { kind: "encrypt"; source: LocalBackupSource; password: string; deviceId: string }
  | { kind: "decrypt"; contents: string; password: string }
  | { kind: "auto-seal"; key: CryptoKey; workspaceId: string; objectId: string; value: unknown }
  | { kind: "auto-open"; key: CryptoKey; workspaceId: string; objectId: string; cipher: BackupCipher }
  | { kind: "digest"; value: unknown };
export type BackupCryptoResponse = { ok: true; result: unknown } | { ok: false; error: string };
const backupWorkers = new Set<Worker>();
let activeActivities = 0;
// Keep PWA updates behind the whole read/upload/restore transaction, including
// periods when no crypto Worker is running.
export function holdBackupActivity() {
  activeActivities++;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      activeActivities--;
    }
  };
}
export const automaticBackupWorker = {
  seal: (key: CryptoKey, workspaceId: string, objectId: string, value: unknown) =>
    runCrypto<BackupCipher>({ kind: "auto-seal", key, workspaceId, objectId, value }),
  open: (key: CryptoKey, workspaceId: string, objectId: string, cipher: BackupCipher) =>
    runCrypto<unknown>({ kind: "auto-open", key, workspaceId, objectId, cipher }),
  digest: (value: unknown) => runCrypto<string>({ kind: "digest", value }),
};

export function hasPendingBackupJobs(): boolean {
  return backupWorkers.size > 0 || activeActivities > 0;
}

function canceled(): DOMException {
  return new DOMException("備份已取消。", "AbortError");
}

function untilCanceled<T>(pending: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(canceled());
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(canceled());
    signal.addEventListener("abort", abort, { once: true });
    // Keep both handlers: a canceled read may still finish or fail in the database Worker.
    pending.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

function runCrypto<T>(request: BackupCryptoRequest, signal?: AbortSignal): Promise<T> {
  if (signal?.aborted) return Promise.reject(canceled());
  return new Promise<T>((resolve, reject) => {
    const worker = new Worker(new URL("./backup.worker.ts", import.meta.url), { type: "module" });
    backupWorkers.add(worker);
    let finished = false;
    const finish = (error?: Error, result?: T) => {
      if (finished) return;
      finished = true;
      signal?.removeEventListener("abort", abort);
      backupWorkers.delete(worker);
      worker.terminate();
      if (error) reject(error);
      else resolve(result as T);
    };
    const abort = () => finish(canceled());
    signal?.addEventListener("abort", abort, { once: true });
    worker.onmessage = (event: MessageEvent<BackupCryptoResponse>) => {
      if (event.data.ok) finish(undefined, event.data.result as T);
      else finish(new Error(event.data.error));
    };
    worker.onerror = () => finish(new Error("備份處理發生錯誤，請重試；現有資料沒有更改。"));
    try {
      worker.postMessage(request);
    } catch {
      finish(new Error("未能開始備份處理；現有資料沒有更改。"));
    }
  });
}

export async function createEncryptedBackup(
  readSource: () => Promise<LocalBackupSource>,
  password: string,
  deviceId: string,
  signal: AbortSignal,
  onStage: (stage: BackupStage) => void,
): Promise<string> {
  signal.throwIfAborted();
  onStage("snapshot");
  const source = await untilCanceled(readSource(), signal);
  signal.throwIfAborted();
  onStage("encrypt");
  return runCrypto<string>({ kind: "encrypt", source, password, deviceId }, signal);
}

export function decryptBackupInWorker(contents: string, password: string): Promise<RestoredBackup> {
  return runCrypto<RestoredBackup>({ kind: "decrypt", contents, password });
}

export function storageCapacityNotice(status: { usageBytes?: number; quotaBytes?: number }): string | undefined {
  const { usageBytes, quotaBytes } = status;
  if (usageBytes === undefined || quotaBytes === undefined || quotaBytes <= 0) return;
  if (usageBytes / quotaBytes >= 0.8 || quotaBytes - usageBytes < 200 * 1024 * 1024)
    return "網站儲存空間接近上限，請先下載加密備份。不要清除網站資料；空間不足時還原可能無法完成。";
}
